/**
 * @file PickingTaskItemsSection.tsx
 * @description Левая колонка экрана задания: поле поиска у закрытого
 * документа, «Лист комплектации» и список позиций (phase19.24 этап 1, шаг 1).
 *
 * Адаптер между экраном и общим блоком `PickingTaskItemsPanel`: страница
 * отвечает за каркас документа и права, а раскладка секции позиций и проброс
 * двух десятков обработчиков — задача одного места, иначе страница снова
 * перевалит за 200 строк.
 */

import { Printer } from "lucide-react";
import type { ReactNode } from "react";

import { PickingItemsSearch } from "./PickingItemsSearch";
import { PickingTaskItemsPanel } from "./PickingTaskItemsPanel";
import type { usePickingTaskScreen } from "./usePickingTaskScreen";
import { Button } from "../../ui/button";
import type { PickingTaskDetailDto } from "../../../lib/api-picking";

interface Props {
  task: PickingTaskDetailDto;
  screen: ReturnType<typeof usePickingTaskScreen>;
  /** Кнопка «Пересобрать задание» для полосы «строки потерялись». */
  rebuildAction: ReactNode;
  printing: boolean;
  onPrint: () => void;
}

export function PickingTaskItemsSection({
  task,
  screen,
  rebuildAction,
  printing,
  onPrint,
}: Props) {
  const { pick, search } = screen;
  return (
    <PickingTaskItemsPanel
      items={search.visibleItems}
      totalCount={task.items.length}
      itemsSource={task.itemsSource}
      // У живого задания поле скана живёт в панели справа — одно на экран.
      searchSlot={
        screen.lockReason ? (
          <PickingItemsSearch
            lockReason={screen.lockReason}
            busy={pick.busy}
            scanRef={screen.scanRef}
            onQueryChange={search.changeQuery}
            onSubmitScan={screen.submitScan}
          />
        ) : undefined
      }
      sectionAction={
        <Button
          variant="outline"
          // Складская кнопка на `lg` — минимум 44px (контракт §1.2 п. 5):
          // `size="sm"` давал 32px, и действие секции мазалось мимо.
          className="hidden min-h-11 lg:inline-flex"
          onClick={onPrint}
          disabled={printing}
        >
          <Printer className="size-4" />
          <span className="ml-1.5">Лист комплектации</span>
        </Button>
      }
      rebuildAction={rebuildAction}
      query={search.query}
      onResetFilter={screen.resetFilter}
      editable={screen.editable}
      busy={pick.busy}
      scannedItemId={pick.scannedItemId}
      onChangeQty={screen.setQty}
      onOpenManualQty={(itemId) => screen.setDialog({ kind: "qty", itemId })}
      onOpenShortage={(itemId) => screen.setDialog({ kind: "itemShortage", itemId })}
      onClearShortage={screen.clearShortage}
      onReturnFocus={screen.focusScan}
    />
  );
}
